import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import { getAdminServices, getUsers } from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { normalizeAdminListResponse } from '../../utils/adminListResponse';
import { useAdminListView } from '../../hooks/useAdminListView';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import BrandBranchFilterFields from '../../components/BrandBranchFilterFields';
import usePortalScope from '../../hooks/usePortalScope';
import { tenantListParams } from '../../utils/contentScope';
import './AdminPages.css';

const UserManagement = () => {
  const portal = usePortalScope();
  const salons = portal.salons;
  const brands = portal.brands;
  const [services, setServices] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    brandId: '',
    salonId: '',
    serviceId: '',
    mobile: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const getUserParams = useCallback(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    Object.assign(params, tenantListParams(portal, filters));
    if (filters.serviceId) params.serviceId = filters.serviceId;
    if (filters.mobile) params.mobile = filters.mobile;
    return params;
  }, [filters, portal]);

  const { items: users, loading, loadingMore, pagination, loadMore } = useAdminInfiniteList({
    load: getUsers,
    resetKey: filtersKey,
    getParams: getUserParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const listView = useAdminListView(users, (user) => user._id, {
    defaultView: 'table',
    resetKey: filtersKey
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const userColumns = useMemo(
    () => [
      { key: 'name', label: 'Name', render: (user) => <strong>{user.profile?.name || 'No Name'}</strong> },
      { key: 'mobile', label: 'Mobile', render: (user) => user.mobile || '-' },
      { key: 'email', label: 'Email', render: (user) => user.profile?.email || '-' },
      { key: 'gender', label: 'Gender', render: (user) => user.profile?.gender || '-' },
      { key: 'registered', label: 'Registered', render: (user) => formatDate(user.createdAt) },
      { key: 'lastLogin', label: 'Last Login', render: (user) => formatDateTime(user.lastLogin) },
      {
        key: 'rating',
        label: 'Rating',
        render: (user) =>
          user.rating > 0 ? <span className="status-badge active">⭐ {user.rating}</span> : '-'
      }
    ],
    []
  );

  const userListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && users.length > 0 && (
        <p className="admin-infinite-end">All users loaded ({pagination.total} total).</p>
      )}
    </>
  );

  const userEmptyState = (
    <div className="admin-card empty-state">
      <p>No users found matching the filters.</p>
      {filters.brandId && (
        <p style={{ marginTop: '8px', fontSize: '13px', color: '#8189A5' }}>
          When a brand is selected, only customers who have a booking at a branch of that brand are shown. If this brand has no branches yet, or no bookings, the list will be empty. Add branches and get bookings to see customers here.
        </p>
      )}
    </div>
  );

  const fetchServices = async () => {
    try {
      const response = await getAdminServices({ all: true });
      const { data } = normalizeAdminListResponse(response);
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      dateFrom: '',
      dateTo: '',
      brandId: '',
      salonId: '',
      serviceId: '',
      mobile: ''
    });
  };

  return (
    <AdminLayout
      pageTitle="User Management"
      subtitle={
        pagination.total
          ? `${users.length} of ${pagination.total} users loaded · scroll for more`
          : 'View and filter all users (customers)'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header">
          <h1>User List</h1>
        </div>

        <div className="admin-card filter-card">
          <h3>Filters</h3>
          <div className="filter-grid">
            <div className="form-group">
              <label>Search Users</label>
              <input
                type="text"
                placeholder="Search by name, mobile, email..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Registration Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Registration Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
            <BrandBranchFilterFields
              portal={portal}
              filters={filters}
              onChange={handleFilterChange}
              brands={brands}
              salons={salons}
              salonLabel="Branch Visited"
            />
            <div className="form-group">
              <label>Service Used</label>
              <select
                value={filters.serviceId}
                onChange={(e) => handleFilterChange('serviceId', e.target.value)}
              >
                <option value="">All Services</option>
                {services
                  .filter((s) => s.isActive !== false)
                  .map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name} (₹{s.price})
                    </option>
                  ))}
              </select>
            </div>
            <div className="form-group">
              <label>Mobile</label>
              <input
                type="text"
                value={filters.mobile}
                onChange={(e) => handleFilterChange('mobile', e.target.value)}
                placeholder="Search by mobile"
              />
            </div>
            <div className="form-group filter-actions">
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <Loader variant="dark" fullScreen={false} />
        ) : (
          <>
            <AdminListViewControls
              viewMode={listView.viewMode}
              onViewModeChange={listView.setViewMode}
              selectedCount={listView.selectedCount}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              totalCount={users.length}
            showSelection={false}
            />
            <AdminSelectableList
              items={users}
              getItemId={(user) => user._id}
              viewMode={listView.viewMode}
            selectable={false}
              isSelected={listView.isSelected}
              onToggleItem={listView.toggleItem}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              columns={userColumns}
              tableClassName="user-table"
              renderGridCard={(user) => (
                <div className="admin-card admin-card--stretch transaction-card">
                  <div className="transaction-header">
                    <div>
                      <h3>{user.profile?.name || 'No Name'}</h3>
                      <p className="transaction-date">Registered: {formatDate(user.createdAt)}</p>
                    </div>
                    {user.rating > 0 && (
                      <span className="status-badge active">⭐ {user.rating}</span>
                    )}
                  </div>
                  <div className="transaction-details">
                    <p><strong>Mobile:</strong> {user.mobile || '-'}</p>
                    {user.profile?.email && <p><strong>Email:</strong> {user.profile.email}</p>}
                    {user.profile?.gender && <p><strong>Gender:</strong> {user.profile.gender}</p>}
                    {user.lastLogin && <p><strong>Last Login:</strong> {formatDateTime(user.lastLogin)}</p>}
                  </div>
                </div>
              )}
              emptyState={userEmptyState}
              scrollRef={scrollRef}
              footer={userListFooter}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default UserManagement;
